/**
 * The shell half of the kernel handoff (kernel apps, WS-16a, design section 3.4, R-003, R-042,
 * R-058, R-064).
 *
 * {@link beginKernelSignIn} sends the browser to the kernel authorize page with a PKCE S256
 * challenge, a state and the return host. The kernel signs the user in and sends the browser back
 * to `SHELL_KERNEL_CALLBACK_PATH` on that host with a one-time code. {@link redeemKernelCallback}
 * checks the callback against the record this tab wrote and trades the code for a shell session.
 * Every wire name comes from the WS-1b contract `kernel/kernel-handoff.ts` of `@zion/sdk-contracts`.
 *
 * No name here carries a redirect URI. The one host on the wire is `return_host`, the host of this
 * page, and core-api accepts it only when it is a served host of the org (R-014, R-058, R-064).
 */
import {
  AUTHORIZE_PARAMS,
  CALLBACK_PARAMS,
  KERNEL_AUTHORIZE_PATH,
  KERNEL_HANDOFF_STATE_PATTERN,
  KERNEL_RETURN_PATH_MAX_LENGTH,
  KERNEL_RETURN_PATH_PATTERN,
  KERNEL_TOKEN_ROUTE,
  PKCE_CODE_VERIFIER_PATTERN,
  SESSION_TRANSPORT_COOKIE,
  SESSION_TRANSPORT_HEADER,
  SHELL_KERNEL_CALLBACK_PATH,
  kernelAuthorizeQuerySchema,
  kernelCallbackQuerySchema,
  kernelTokenRequestSchema,
  sessionResponseSchema,
  type KernelAuthorizeQuery,
  type SessionResponse,
} from '@zion/sdk-contracts';
import { resolvePostLoginPath } from '@zion/shell-system-surfaces/protocol';
import { kernelUrl, type KernelOrigin } from './kernel-origin.js';
import { createPkcePair } from './pkce.js';

/** The `sessionStorage` key of the one handoff record of this tab. */
export const HANDOFF_STORAGE_KEY = 'zion_kernel_handoff' as const;

/** How long a handoff record lives, in milliseconds: 600,000 (ten minutes). */
export const HANDOFF_TTL_MS = 600_000 as const;

/** The longest callback search, `?` included, in characters: 2,048. */
export const MAX_CALLBACK_SEARCH_LENGTH = 2048 as const;

/**
 * The bound of the token request, in milliseconds: 10,000. A request that runs longer aborts and
 * reads `unavailable`, so a hung server never holds the boot's routing chain.
 */
export const KERNEL_TOKEN_TIMEOUT_MS = 10_000 as const;

/** The three members of Web Storage the handoff uses. The boot passes `window.sessionStorage`. */
export type HandoffStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * What the handoff runs against.
 *
 * - `storage`: the tab's `sessionStorage`; it holds one record per tab.
 * - `crypto`: the page's `Crypto`, for the random draw and the SHA-256.
 * - `fetch`: the same-origin fetch of the boot.
 * - `now`: the clock in epoch milliseconds.
 * - `kernelOrigin`: the kernel origin of this data plane.
 * - `returnHost`: the host of this page (`location.hostname`), the host that waits for the code
 *   (R-064). It must be a lower-case host with no port.
 */
export interface HandoffDeps {
  readonly storage: HandoffStorage;
  readonly crypto: Crypto;
  readonly fetch: typeof fetch;
  readonly now: () => number;
  readonly kernelOrigin: KernelOrigin;
  readonly returnHost: string;
}

/**
 * Why a handoff step stopped.
 *
 * - `no-crypto`: the random draw or the SHA-256 failed.
 * - `storage`: the record write threw.
 * - `return-host`: the page host fails the contract's host rule (a port, an IPv6 literal).
 * - `no-record`: the tab holds no record, or the record does not parse.
 * - `malformed-callback`: the callback query fails the length cap, the name set or the schema.
 * - `state-mismatch`: the callback state differs from the record.
 * - `expired`: the record is older than {@link HANDOFF_TTL_MS}.
 * - `token-refused`: core-api refused the code (400, 401, 403, 404, 409 or 410).
 * - `unavailable`: the token request failed, timed out or answered another non-2xx status.
 * - `malformed-session`: a 2xx answer that is not a session.
 */
export type KernelHandoffFailure =
  | 'no-crypto'
  | 'storage'
  | 'return-host'
  | 'no-record'
  | 'malformed-callback'
  | 'state-mismatch'
  | 'expired'
  | 'token-refused'
  | 'unavailable'
  | 'malformed-session';

/** The one error the handoff rejects with. Its message names the failure and nothing else. */
export class KernelHandoffError extends Error {
  override readonly name = 'KernelHandoffError';
  readonly failure: KernelHandoffFailure;

  constructor(failure: KernelHandoffFailure) {
    super(`the kernel handoff stopped: ${failure}`);
    this.failure = failure;
  }
}

/** A redeemed code: the session core-api answered, and the path the tab lands on. */
export interface RedeemedSession {
  readonly session: SessionResponse;
  readonly landing: string;
}

/** The record one tab keeps between the two halves of a handoff. */
interface HandoffRecord {
  readonly v: 1;
  readonly state: string;
  readonly verifier: string;
  readonly returnTo: string;
  readonly createdAt: number;
}

/** The statuses that mean core-api refused this code: a retry with the same code cannot pass. */
const REFUSED_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 404, 409, 410]);

/** The lower-case path without one trailing slash, as the router compares it. */
function routerPath(pathname: string): string {
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed.toLowerCase();
}

/**
 * True when `pathname` is `SHELL_KERNEL_CALLBACK_PATH`, case folded, with one trailing slash
 * removed. The path sits under the reserved `auth` segment, so no app can mount there. Pure.
 */
export function isKernelCallbackPath(pathname: string): boolean {
  return routerPath(pathname) === SHELL_KERNEL_CALLBACK_PATH;
}

/** `returnTo` when it fits the contract's return-path rule, else the default landing of a login. */
function returnPathOf(returnTo: string): string {
  return returnTo.length <= KERNEL_RETURN_PATH_MAX_LENGTH && KERNEL_RETURN_PATH_PATTERN.test(returnTo)
    ? returnTo
    : resolvePostLoginPath(null);
}

/**
 * Start a kernel sign-in and answer the URL of the kernel authorize page.
 *
 * The boot calls it in the `on` arm, for a signed-out visitor and for `/login`. The caller passes a
 * `returnTo` that `resolvePostLoginPath` already accepted. Steps:
 *
 * 1. `createPkcePair(deps.crypto)`. Any rejection rejects `no-crypto`.
 * 2. A `returnTo` over `KERNEL_RETURN_PATH_MAX_LENGTH` characters or outside
 *    `KERNEL_RETURN_PATH_PATTERN` becomes `/home`, so the kernel page never refuses the query for it.
 * 3. `kernelAuthorizeQuerySchema` must accept the query; a `returnHost` that fails the host rule
 *    rejects `return-host`.
 * 4. Write `{ v: 1, state, verifier, returnTo, createdAt: deps.now() }` as JSON under
 *    {@link HANDOFF_STORAGE_KEY}. The write replaces a record of an earlier handoff in this tab. A
 *    throw from `setItem` rejects `storage`.
 * 5. Answer `kernelUrl(kernelOrigin, KERNEL_AUTHORIZE_PATH, '?' + query)`. The query holds the five
 *    `AUTHORIZE_PARAMS` in their order and no other name.
 *
 * It sends no request. The record holds no credential of the kernel origin.
 */
export async function beginKernelSignIn(deps: HandoffDeps, returnTo: string): Promise<string> {
  let pair: Awaited<ReturnType<typeof createPkcePair>>;
  try {
    pair = await createPkcePair(deps.crypto);
  } catch {
    throw new KernelHandoffError('no-crypto');
  }
  const query: KernelAuthorizeQuery = {
    code_challenge: pair.challenge,
    code_challenge_method: 'S256',
    state: pair.state,
    return_to: returnPathOf(returnTo),
    return_host: deps.returnHost,
  };
  if (!kernelAuthorizeQuerySchema.safeParse(query).success) throw new KernelHandoffError('return-host');
  const record: HandoffRecord = { v: 1, state: pair.state, verifier: pair.verifier, returnTo: query.return_to, createdAt: deps.now() };
  try {
    deps.storage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(record));
  } catch {
    throw new KernelHandoffError('storage');
  }
  const search = new URLSearchParams(AUTHORIZE_PARAMS.map((name) => [name, query[name]]));
  return kernelUrl(deps.kernelOrigin, KERNEL_AUTHORIZE_PATH, `?${search.toString()}`);
}

/** True when `value` is a record this module wrote: every member present, with its grammar. */
function isHandoffRecord(value: unknown): value is HandoffRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Readonly<Record<string, unknown>>;
  return (
    record.v === 1 &&
    typeof record.state === 'string' &&
    KERNEL_HANDOFF_STATE_PATTERN.test(record.state) &&
    typeof record.verifier === 'string' &&
    PKCE_CODE_VERIFIER_PATTERN.test(record.verifier) &&
    typeof record.returnTo === 'string' &&
    typeof record.createdAt === 'number' &&
    Number.isFinite(record.createdAt)
  );
}

/** Read the record and remove it in the same step, so one record serves one callback at most. */
function takeRecord(storage: HandoffStorage): HandoffRecord {
  let parsed: unknown;
  try {
    const raw = storage.getItem(HANDOFF_STORAGE_KEY);
    storage.removeItem(HANDOFF_STORAGE_KEY);
    parsed = raw === null ? null : JSON.parse(raw);
  } catch {
    throw new KernelHandoffError('no-record');
  }
  if (!isHandoffRecord(parsed)) throw new KernelHandoffError('no-record');
  return parsed;
}

/** The code and state of a callback search that holds each `CALLBACK_PARAMS` name once and no other. */
function callbackQueryOf(search: string): { readonly code: string; readonly state: string } {
  const params = new URLSearchParams(search);
  const names = [...params.keys()];
  const exact =
    names.length === CALLBACK_PARAMS.length && CALLBACK_PARAMS.every((name) => params.getAll(name).length === 1);
  const parsed = exact
    ? kernelCallbackQuerySchema.safeParse(Object.fromEntries(CALLBACK_PARAMS.map((name) => [name, params.get(name)])))
    : null;
  if (parsed === null || !parsed.success) throw new KernelHandoffError('malformed-callback');
  return parsed.data;
}

/** POST the code and the verifier, and answer the parsed session. */
async function redeemCode(deps: HandoffDeps, code: string, codeVerifier: string): Promise<SessionResponse> {
  const body = kernelTokenRequestSchema.parse({ code, codeVerifier });
  let response: Response;
  try {
    response = await deps.fetch(KERNEL_TOKEN_ROUTE, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(KERNEL_TOKEN_TIMEOUT_MS),
    });
  } catch {
    throw new KernelHandoffError('unavailable');
  }
  if (!response.ok) throw new KernelHandoffError(REFUSED_STATUSES.has(response.status) ? 'token-refused' : 'unavailable');
  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new KernelHandoffError('malformed-session');
  }
  const session = sessionResponseSchema.safeParse(json);
  if (!session.success) throw new KernelHandoffError('malformed-session');
  return session.data;
}

/**
 * Redeem the code of a callback and answer the session and the landing.
 *
 * The boot calls it once, on the callback path, with `location.search`. It checks in this order:
 *
 * 1. A `search` over {@link MAX_CALLBACK_SEARCH_LENGTH} characters rejects `malformed-callback`.
 * 2. It reads the record and removes it at once, so one code redeems at most once per tab. No
 *    record, a storage that throws, bad JSON, a `v` other than `1`, a verifier or a state outside
 *    the WS-1b patterns, or a bad `returnTo` or `createdAt` rejects `no-record`.
 * 3. Each name of `CALLBACK_PARAMS` must occur once, and no other name may occur. Then
 *    `kernelCallbackQuerySchema` must accept the values. Any failure rejects `malformed-callback`,
 *    a `redirect_uri` or an `error` name included.
 * 4. A `state` other than the record's rejects `state-mismatch`.
 * 5. A record older than {@link HANDOFF_TTL_MS} rejects `expired`.
 * 6. It POSTs `KERNEL_TOKEN_ROUTE` same-origin with `SESSION_TRANSPORT_HEADER: cookie` and the
 *    body `{ code, codeVerifier }`, bounded by {@link KERNEL_TOKEN_TIMEOUT_MS}. A rejected or
 *    timed-out fetch rejects `unavailable`. A 400, 401, 403, 404, 409 or 410 rejects
 *    `token-refused`; any other non-2xx rejects `unavailable`. A 2xx body that is not JSON or that
 *    `sessionResponseSchema` refuses rejects `malformed-session`.
 * 7. It answers `{ session, landing: resolvePostLoginPath(record.returnTo) }`. The query's
 *    `return_to` never sets the landing. core-api sets the host-only refresh cookie in its reply.
 *
 * It never adopts the session; the boot does. Every rejection is a {@link KernelHandoffError}.
 */
export async function redeemKernelCallback(deps: HandoffDeps, search: string): Promise<RedeemedSession> {
  if (search.length > MAX_CALLBACK_SEARCH_LENGTH) throw new KernelHandoffError('malformed-callback');
  const record = takeRecord(deps.storage);
  const query = callbackQueryOf(search);
  if (query.state !== record.state) throw new KernelHandoffError('state-mismatch');
  if (deps.now() - record.createdAt > HANDOFF_TTL_MS) throw new KernelHandoffError('expired');
  const session = await redeemCode(deps, query.code, record.verifier);
  return { session, landing: resolvePostLoginPath(record.returnTo) };
}
